import apiService from './apiService';

// TypeScript interfaces
export interface BankAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  bankName: string;
  branchName?: string;
  swiftCode?: string;
  journalId?: string;
  currencyId?: string;
  companyId: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  statements?: BankStatement[];
}

export interface BankStatement {
  id: string;
  name: string;
  bankAccountId: string;
  date: string;
  balanceStart: number;
  balanceEnd: number;
  state: 'OPEN' | 'RECONCILED';
  companyId: string;
  createdAt: string;
  updatedAt: string;
  bankAccount?: BankAccount;
  lines?: BankStatementLine[];
}

export interface BankStatementLine {
  id: string;
  statementId: string;
  date: string;
  name: string;
  ref?: string;
  partnerId?: string;
  amount: number;
  isReconciled: boolean;
  journalEntryLineId?: string;
  createdAt: string;
}

export interface CreateBankAccountInput {
  accountNumber: string;
  accountName: string;
  bankName: string;
  branchName?: string;
  swiftCode?: string;
  journalId?: string;
  currencyId?: string;
}

export interface UpdateBankAccountInput {
  accountNumber?: string;
  accountName?: string;
  bankName?: string;
  branchName?: string;
  swiftCode?: string;
  journalId?: string;
  currencyId?: string;
  active?: boolean;
}

export interface CreateBankStatementInput {
  bankAccountId: string;
  date: string;
  balanceStart: number;
  balanceEnd: number;
  lines?: {
    date: string;
    description: string;
    reference?: string;
    amount: number;
    partnerId?: string;
  }[];
}

export interface StatementFilters {
  bankAccountId?: string;
  state?: string;
  fromDate?: string;
  toDate?: string;
}

export interface ReconciliationSummary {
  statementId: string;
  statementName: string;
  bankAccountName: string;
  balanceStart: number;
  balanceEnd: number;
  totalLines: number;
  reconciledLines: number;
  unreconciledLines: number;
  reconciledAmount: number;
  unreconciledAmount: number;
  state: string;
  difference: number;
}

export interface AutoReconcileResult {
  matchedCount: number;
  matches: {
    statementLineId: string;
    journalEntryLineId: string;
    statementLineDescription: string;
    journalEntryRef: string;
    amount: number;
  }[];
}

export interface MatchingCandidate {
  id: string;
  debit: number;
  credit: number;
  reconciled: boolean;
  entry: {
    id: string;
    name: string;
    date: string;
    ref?: string;
    narration?: string;
    invoiceNo?: string;
    partnerId?: string;
  };
  account: {
    id: string;
    name: string;
    code: string;
  };
}

export interface DashboardStats {
  totalBankAccounts: number;
  activeBankAccounts: number;
  openStatements: number;
  reconciledStatements: number;
  pendingTransactions: number;
}

class BankReconciliationService {
  private bankAccountsUrl = '/accounting/bank-accounts';
  private statementsUrl = '/accounting/bank-statements';
  private statementLinesUrl = '/accounting/bank-statement-lines';

  // ==================== BANK ACCOUNTS ====================

  /**
   * Get all bank accounts
   */
  async getAllBankAccounts(): Promise<BankAccount[]> {
    const response = await apiService.get(this.bankAccountsUrl);
    return response.data;
  }

  /**
   * Get bank account by ID
   */
  async getBankAccountById(id: string): Promise<BankAccount> {
    const response = await apiService.get(`${this.bankAccountsUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new bank account
   */
  async createBankAccount(data: CreateBankAccountInput): Promise<BankAccount> {
    const response = await apiService.post(this.bankAccountsUrl, data);
    return response.data;
  }

  /**
   * Update an existing bank account
   */
  async updateBankAccount(id: string, data: UpdateBankAccountInput): Promise<BankAccount> {
    const response = await apiService.put(`${this.bankAccountsUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a bank account
   */
  async deleteBankAccount(id: string): Promise<void> {
    await apiService.delete(`${this.bankAccountsUrl}/${id}`);
  }

  // ==================== BANK STATEMENTS ====================

  /**
   * Get all bank statements with optional filters
   */
  async getAllStatements(filters?: StatementFilters): Promise<BankStatement[]> {
    const params = new URLSearchParams();
    if (filters?.bankAccountId) params.append('bankAccountId', filters.bankAccountId);
    if (filters?.state) params.append('state', filters.state);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const queryString = params.toString();
    const url = queryString ? `${this.statementsUrl}?${queryString}` : this.statementsUrl;

    const response = await apiService.get(url);
    return response.data;
  }

  /**
   * Get statement by ID
   */
  async getStatementById(id: string): Promise<BankStatement> {
    const response = await apiService.get(`${this.statementsUrl}/${id}`);
    return response.data;
  }

  /**
   * Create a new bank statement
   */
  async createStatement(data: CreateBankStatementInput): Promise<BankStatement> {
    const response = await apiService.post(this.statementsUrl, data);
    return response.data;
  }

  /**
   * Update an existing statement
   */
  async updateStatement(id: string, data: Partial<BankStatement>): Promise<BankStatement> {
    const response = await apiService.put(`${this.statementsUrl}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a statement
   */
  async deleteStatement(id: string): Promise<void> {
    await apiService.delete(`${this.statementsUrl}/${id}`);
  }

  /**
   * Add a line to a statement
   */
  async addStatementLine(
    statementId: string,
    data: { date: string; description: string; reference?: string; amount: number; partnerId?: string }
  ): Promise<BankStatement> {
    const response = await apiService.post(`${this.statementsUrl}/${statementId}/lines`, data);
    return response.data;
  }

  /**
   * Import statement from CSV data
   */
  async importStatement(data: CreateBankStatementInput): Promise<BankStatement> {
    const response = await apiService.post(`${this.statementsUrl}/import`, data);
    return response.data;
  }

  // ==================== STATEMENT LINES ====================

  /**
   * Correct a statement line that was entered wrongly. Refused on a reconciled
   * statement, or on a line that is already matched — unmatch it first.
   */
  async updateStatementLine(
    lineId: string,
    data: { date?: string; name?: string; ref?: string; amount?: number }
  ): Promise<BankStatementLine> {
    const response: any = await apiService.put(`${this.statementLinesUrl}/${lineId}`, data);
    return response.data;
  }

  /**
   * Delete a statement line
   */
  async deleteStatementLine(lineId: string): Promise<void> {
    await apiService.delete(`${this.statementLinesUrl}/${lineId}`);
  }

  // ==================== RECONCILIATION ====================

  /**
   * Get reconciliation summary for a statement
   */
  async getReconciliationSummary(statementId: string): Promise<ReconciliationSummary> {
    const response = await apiService.get(`${this.statementsUrl}/${statementId}/reconciliation`);
    return response.data;
  }

  /**
   * Auto-reconcile a statement
   */
  async autoReconcile(statementId: string): Promise<AutoReconcileResult> {
    const response = await apiService.post(`${this.statementsUrl}/${statementId}/auto-reconcile`);
    return response.data;
  }

  /**
   * Match a statement line to a journal entry line
   */
  async matchTransaction(lineId: string, journalEntryLineId: string): Promise<void> {
    await apiService.post(`${this.statementLinesUrl}/${lineId}/match`, { journalEntryLineId });
  }

  /**
   * Unmatch a statement line
   */
  async unmatchTransaction(lineId: string): Promise<void> {
    await apiService.post(`${this.statementLinesUrl}/${lineId}/unmatch`);
  }

  /**
   * Get matching candidates for a statement line
   */
  async getMatchingCandidates(lineId: string): Promise<MatchingCandidate[]> {
    const response = await apiService.get(`${this.statementLinesUrl}/${lineId}/candidates`);
    return response.data;
  }

  /**
   * Finalize reconciliation for a statement
   */
  async finalizeReconciliation(statementId: string): Promise<BankStatement> {
    const response = await apiService.post(`${this.statementsUrl}/${statementId}/finalize`);
    return response.data;
  }

  /**
   * Reopen a reconciled statement
   */
  async reopenReconciliation(statementId: string): Promise<BankStatement> {
    const response = await apiService.post(`${this.statementsUrl}/${statementId}/reopen`);
    return response.data;
  }

  // ==================== DASHBOARD ====================

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiService.get('/accounting/bank-reconciliation/stats');
    return response.data;
  }
}

export default new BankReconciliationService();